import { authAPI } from './auth-api';
import { getDeviceId } from '../utils/deviceId';

const trimTrailingSlash = (value) => String(value || '').trim().replace(/\/+$/, '');

/** Origin only — do not use String.replace('/api') or `api-ecatcho` becomes `-ecatcho`. */
export const getApiOrigin = (raw = process.env.REACT_APP_API_BASE_URL) => {
  const url = trimTrailingSlash(raw);
  if (!url) return 'http://localhost:5010';
  return url.replace(/\/api$/i, '');
};

export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5010/api',
  ADMIN_BASE_URL: process.env.REACT_APP_ADMIN_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'),
  STAFF_RIDER_BASE_URL: process.env.REACT_APP_STAFF_RIDER_BASE_URL || getApiOrigin(),
  ENDPOINTS: {
    ADMIN_LOGIN: '/v1/admin/login',
    ADMIN_VERIFY: '/v1/admin/verify',
    ADMIN_SWITCH_STORE: '/v1/admin/switch-store',
    ADMIN_CREATE: '/v1/admin/create-admin',
    ADMIN_LIST: '/v1/admin/list',
    ADMIN_UPDATE: '/v1/admin/update',
    ADMIN_DELETE: '/v1/admin',
    ORDERS: '/v1/admin/orders',
    DRIVERS: '/v1/admin/drivers',
    DISPATCH: '/v1/admin/dispatch',
    GENERAL_CONFIG: '/v1/admin/generalConfig',
    PRODUCTS: '/v1/admin/products',
    BANNER_CREATE: '/v1/admin/banner/create',
    BANNER_UPDATE: '/v1/admin/banner/update',
    BANNER_DELETE: '/v1/admin/banner/',
    BANNER_LIST: '/v1/admin/banner/list',
    BANNER_REORDER: '/v1/admin/banner/reorder',
    REEL_VIDEO_CREATE: '/v1/admin/reelVideo/create',
    REEL_VIDEO_ACTIVE: '/v1/admin/reelVideo/active',
    REEL_VIDEO_INACTIVE: '/v1/admin/reelVideo/inactive',
    REEL_VIDEO_UPDATE: '/v1/admin/reelVideo/update',
    REEL_VIDEO_REORDER: '/v1/admin/reelVideo/reorder',
    REEL_VIDEO_DELETE: '/v1/admin/reelVideo',
    EVENT_LOGS: '/v1/admin/events/logs',
    EVENT_LOG_USERS: '/v1/admin/events/users',
    REPORTS_DAILY_SALE: '/v1/admin/reports/daily-sale',
    USER_ACTION_LOGS_COUNT: '/v4/admin/analytics/action-logs',
  },
};

const getAuthHeaders = () => {
  const token = authAPI.getToken();
  const headers = {
    'Content-Type': 'application/json',
    'x-device-id': getDeviceId(),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const getAuthNoHeaders = () => {
  const token = authAPI.getToken();
  const headers = {
    'x-device-id': getDeviceId(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

export const buildApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

/** Prefer server `msg` / `message`; fall back to parsed validation text from `error`. */
export const getApiErrorMessage = (result, fallback = 'Request failed') => {
  if (result == null) return fallback;
  if (result instanceof Error) {
    const text = String(result.message || '').trim();
    return text || fallback;
  }
  if (typeof result === 'string') {
    const text = result.trim();
    return text || fallback;
  }
  const msg = String(result.msg ?? result.message ?? '').trim();
  if (msg) return msg;
  const err = String(result.error ?? '').trim();
  if (!err) return fallback;
  const validationTail = err.match(/validation failed:\s*(.+)$/is);
  if (validationTail?.[1]) return validationTail[1].trim();
  return err;
};

export const apiCall = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const isFormDataRequest = options.body instanceof FormData;
  const retries = options.retries ?? (options.method === 'GET' || !options.method ? 1 : 0);
  const defaultOptions = {
    method: 'GET',
    headers: isFormDataRequest ? getAuthNoHeaders() : getAuthHeaders(),
  };

  const finalOptions = { ...defaultOptions, ...options };
  delete finalOptions.retries;

  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(url, finalOptions);

      if (response.status === 401) {
        authAPI.handleSessionExpired();
        return { success: false, error: 'Unauthorized', message: 'Session expired' };
      }

      const data = await response.json();

      if (!response.ok) {
        return data;
      }

      return data;
    } catch (error) {
      lastError = error;
      console.error('API call failed:', error);
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
        continue;
      }
    }
  }

  throw lastError;
};
